import apiClient from "./api";
import { getApiAccessToken } from "./MapComponentsService";
import { PriceGroupDto } from "../types/PriceGroup";
import { WorkPhaseItemDto } from "../types/WorkPhaseItem";
import { AddWorkItemRequest } from "../types/AddWorkItemRequest";
import { DiaryWorkPhaseDto } from "../types/DiaryWorkPhaseDto";
import { PerceptionInvoiceDto } from "../types/PerceptionInvoiceDto";
import { ObjectDiaryEntryDto } from "../types/ObjectDiaryEntryDto";
import { UpdateDiaryAndPerceptionRequest } from "../types/UpdateDiaryAndPerceptionRequest";
import { DiaryTemplateResponseDto } from "../types/DiaryTemplateResponseDto";
import { DiaryTemplateNameDto } from "../types/DiaryTemplateNameDto";
import { ForemanDto } from "../types/ForemanDto";
import {
  UpsertObjectDiaryRequest,
  UpsertObjectDiaryResult,
} from "../types/UpsertObjectDiaryRequest";
import { ObjectDiaryDetailDto } from "../types/ObjectDiaryDetailDto";
import { RealisedWorkPhaseItemDto } from "../types/RealisedWorkPhaseItem";

const baseUrl = "/api/ObjectModel";

// DTOs for crews and machines used in diary group source
export interface ContractCrewDto {
  id: number;
  contractId: number;
  name: string;
  description: string;
}

export interface ContractMachineDto {
  id: number;
  contractId: number;
  name: string;
  description: string;
}

export const getWorkPhaseNames = async (
  contractId: number,
  objectId: number,
  workItemType = 0,
): Promise<PriceGroupDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/getWorkPhaseName/${contractId}/${objectId}?workItemType=${workItemType}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to fetch work phase names: ${response.statusText}`);
  }
  return response.data as PriceGroupDto[];
};

export const getAllWorkPhaseItems = async (
  generalPriceGroupId: number,
  contractId: number,
  objectId: number,
  workItemType = 0,
): Promise<WorkPhaseItemDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/getAllWorkPhaseItems/${generalPriceGroupId}/${contractId}/${objectId}?workItemType=${workItemType}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to fetch work phase items: ${response.statusText}`);
  }
  return response.data as WorkPhaseItemDto[];
};

export const getAllOperationItemsFromPriceGroup = async (
  contractId: number,
  objectId: number,
  workItemType = 0,
): Promise<WorkPhaseItemDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/getAllOperationItemsFromPriceGroup/${contractId}/${objectId}?workItemType=${workItemType}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch operation items from price group: ${response.statusText}`,
    );
  }
  return response.data as WorkPhaseItemDto[];
};

export const getAllOtherWorkPhaseItems = async (
  contractId: number,
  objectId: number,
  workItemType = 0,
): Promise<WorkPhaseItemDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/getAllOtherWorkPhaseItems/${contractId}/${objectId}?workItemType=${workItemType}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch other work phase items: ${response.statusText}`,
    );
  }
  return response.data as WorkPhaseItemDto[];
};

// Align with MapComponentsService create* APIs: accept ready-to-send payload
export const createWorkItem = async (
  payload: any,
  currentUserId?: number,
): Promise<number> => {
  const token = await getApiAccessToken();
  const url =
    typeof currentUserId === "number" && currentUserId > 0
      ? `${baseUrl}/work-items?currentUserId=${encodeURIComponent(currentUserId)}`
      : `${baseUrl}/work-items`;
  const response = await apiClient.post<{ id: number }>(url, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status !== 200) {
    throw new Error(`Failed to create work item: ${response.statusText}`);
  }
  return response.data.id;
};

export const updateWorkItem = async (
  id: number,
  payload: any,
  currentUserId?: number,
): Promise<boolean> => {
  const token = await getApiAccessToken();
  // console.log("Updating work item with payload:", payload);
  // console.log("Work item ID:", id);
  const url =
    typeof currentUserId === "number" && currentUserId > 0
      ? `${baseUrl}/work-items/${id}?currentUserId=${encodeURIComponent(currentUserId)}`
      : `${baseUrl}/work-items/${id}`;
  const response = await apiClient.put<{ updated: boolean }>(url, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status !== 200) {
    throw new Error(`Failed to update work item: ${response.statusText}`);
  }
  return response.data.updated;
};

export const deleteWorkItem = async (
  id: number,
  contractId: number,
  objectId: number,
): Promise<boolean> => {
  const token = await getApiAccessToken();
  const response = await apiClient.delete<{ deleted: boolean }>(
    `${baseUrl}/work-items/${id}?contractId=${contractId}&objectId=${objectId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to delete work item: ${response.statusText}`);
  }
  return response.data.deleted;
};

export const getDiaryWorkPhases = async (
  addressSystemId: number,
  contractId: number,
  objectId: number,
): Promise<DiaryWorkPhaseDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/diary/workphases/${addressSystemId}/${contractId}/${objectId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch diary work phases: ${response.statusText}`,
    );
  }
  return response.data as DiaryWorkPhaseDto[];
};

export const getPerceptionInvoice = async (
  addressSystemId: number,
  id: number,
): Promise<PerceptionInvoiceDto> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/perception/invoice/${addressSystemId}/${id}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch perception invoice: ${response.statusText}`,
    );
  }
  return response.data as PerceptionInvoiceDto;
};

// Fetch contract crews by perception/object id
export const getContractCrewsByPerception = async (
  objectId: number,
): Promise<ContractCrewDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(`${baseUrl}/diary/crews/${objectId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status !== 200) {
    throw new Error(`Failed to fetch crews: ${response.statusText}`);
  }
  return response.data as ContractCrewDto[];
};

// Fetch contract machines by perception/object id
export const getContractMachinesByPerception = async (
  objectId: number,
): Promise<ContractMachineDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/diary/machines/${objectId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to fetch machines: ${response.statusText}`);
  }
  return response.data as ContractMachineDto[];
};

// Fetch diary template details or minimal perception/user info based on templateId
// GET /api/objectmodel/diary/template-or-info/{addressSystemId}/{id}?templateId=...&currentUserId=...

export const getDiaryTemplateOrInfo = async (
  addressSystemId: number,
  id: number,
  templateId: number = 0,
  currentUserId: number = 0,
): Promise<DiaryTemplateResponseDto> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/diary/template-or-info/${addressSystemId}/${id}?templateId=${encodeURIComponent(
      templateId,
    )}&currentUserId=${encodeURIComponent(currentUserId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch diary template/info: ${response.statusText}`,
    );
  }
  return response.data as DiaryTemplateResponseDto;
};

// Fetch diary template name list for select options
export const getDiaryTemplateName = async (
  addressSystemId: number,
  id: number,
  contractId: number,
  currentUserId: number,
): Promise<DiaryTemplateNameDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/diary/template-name/${addressSystemId}/${id}/${contractId}?currentUserId=${encodeURIComponent(
      currentUserId,
    )}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch diary template name list: ${response.statusText}`,
    );
  }
  return response.data as DiaryTemplateNameDto[];
};

// Fetch supervisors (or contractor supervisors) for perception/object
export const getSupervisorsByPerception = async (
  addressSystemId: number,
  objectId: number,
  contractor: boolean = false,
): Promise<ForemanDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/supervisors/${addressSystemId}/${objectId}?contractor=${contractor}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to fetch supervisors: ${response.statusText}`);
  }
  return response.data as ForemanDto[];
};

export const getObjectDiaryEntries = async (
  contractId: number,
  objectId: number,
): Promise<ObjectDiaryEntryDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/diary/entries/${contractId}/${objectId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch object diary entries: ${response.statusText}`,
    );
  }
  return response.data as ObjectDiaryEntryDto[];
};

// Save diary work phases and perception info together
export const saveDiaryWorkPhasesAndPerception = async (
  payload: UpdateDiaryAndPerceptionRequest,
): Promise<boolean> => {
  const token = await getApiAccessToken();
  const response = await apiClient.post<{ saved: boolean }>(
    `${baseUrl}/diary/workphases/save`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to save diary work phases and perception: ${response.statusText}`,
    );
  }
  return response.data.saved;
};

// Upsert (insert/update) a diary entry via POST /api/objectmodel/diary/upsert
export const upsertObjectDiary = async (
  payload: UpsertObjectDiaryRequest,
  currentUserId?: number,
): Promise<UpsertObjectDiaryResult> => {
  const token = await getApiAccessToken();
  const url =
    typeof currentUserId === "number" && currentUserId > 0
      ? `${baseUrl}/diary/upsert?currentUserId=${encodeURIComponent(
          currentUserId,
        )}`
      : `${baseUrl}/diary/upsert`;
  const response = await apiClient.post<UpsertObjectDiaryResult>(url, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status !== 200) {
    throw new Error(`Failed to save diary: ${response.statusText}`);
  }
  return response.data;
};

// Delete diary by id
export const deleteObjectDiaryById = async (
  id: number,
  contractId: number,
  objectId: number,
): Promise<boolean> => {
  const token = await getApiAccessToken();
  const response = await apiClient.delete<{ deleted: boolean }>(
    `${baseUrl}/diary/${id}?contractId=${encodeURIComponent(
      contractId,
    )}&objectId=${encodeURIComponent(objectId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to delete diary: ${response.statusText}`);
  }
  return response.data.deleted;
};

// Get full diary by id for editing
export const getObjectDiaryById = async (
  id: number,
  contractId?: number,
  objectId?: number,
): Promise<ObjectDiaryDetailDto> => {
  const token = await getApiAccessToken();
  const params: Record<string, any> = {};
  if (typeof contractId === "number" && contractId > 0)
    params.contractId = contractId;
  if (typeof objectId === "number" && objectId > 0) params.objectId = objectId;
  const response = await apiClient.get<ObjectDiaryDetailDto>(
    `${baseUrl}/diary/${id}`,
    { headers: { Authorization: `Bearer ${token}` }, params },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to fetch diary: ${response.statusText}`);
  }
  return response.data;
};

// Realised work phase endpoints
export const getRealisedWorkPhases = async (
  contractId: number,
  objectId: number,
): Promise<RealisedWorkPhaseItemDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/realised/workphases/${contractId}/${objectId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch realised work phases: ${response.statusText}`,
    );
  }
  return response.data as RealisedWorkPhaseItemDto[];
};

export const getRealisedWorkPhaseItemsByGroup = async (
  generalPriceGroupId: number,
  contractId: number,
  objectId: number,
): Promise<RealisedWorkPhaseItemDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/realised/workphases/${generalPriceGroupId}/${contractId}/${objectId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch realised work phase items by group: ${response.statusText}`,
    );
  }
  return response.data as RealisedWorkPhaseItemDto[];
};

export const getRealisedOtherWorkPhaseItems = async (
  contractId: number,
  objectId: number,
): Promise<RealisedWorkPhaseItemDto[]> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get(
    `${baseUrl}/realised/others/${contractId}/${objectId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch realised other work phase items: ${response.statusText}`,
    );
  }
  return response.data as RealisedWorkPhaseItemDto[];
};

export interface UnitPriceListResponse {
  objectName: string;
  availableContracts: ContractOption[];
  priceGroups: PriceGroupWithItems[];
  selectedContractId: number | null;
  hideWorkItemNumber: boolean;
  useWorkItemCode: boolean;
}

export interface ContractOption {
  id: number;
  name: string;
}

export interface PriceGroupWithItems {
  id: number;
  name: string;
  orderInGroup: number | null;
  items: PriceItemDetail[];
}

export interface PriceItemDetail {
  id: number;
  itemNumber: number;
  itemSubnumber: number;
  itemCode: string | null;
  name: string;
  unitName: string;
  unitPrice: number;
  orderInGroup: number;
}

export const getUnitPriceList = async (
  objectId: number,
  userId: number,
  addressSystemId: number,
  contractId?: number,
): Promise<UnitPriceListResponse> => {
  const token = await getApiAccessToken();
  const params = new URLSearchParams({
    userId: userId.toString(),
    addressSystemId: addressSystemId.toString(),
  });
  if (contractId) {
    params.append("contractId", contractId.toString());
  }

  const response = await apiClient.get(
    `${baseUrl}/unit-prices/${objectId}?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (response.status !== 200) {
    throw new Error(`Failed to fetch unit price list: ${response.statusText}`);
  }
  return response.data as UnitPriceListResponse;
};
